import { ReadonlyURLSearchParams } from "next/navigation"
import { useCallback, useContext } from "react"
import { ParsedUrlQuery, ParsedUrlQueryInput } from "node:querystring"
import { EnvContext } from "../components/EnvContext"
import { getIsPublic } from "./useIsPublic"

export function searchParamsToQuery(
  searchParams: ReadonlyURLSearchParams
): ParsedUrlQuery {
  const query: ParsedUrlQuery = {}
  for (const [key, value] of searchParams.entries()) {
    const existing = query[key]
    if (existing === undefined) {
      query[key] = value
    } else if (Array.isArray(existing)) {
      existing.push(value)
    } else {
      query[key] = [existing, value]
    }
  }
  return query
}

type RouteUrlObject = {
  href: string
  query?: string | null | ParsedUrlQueryInput | undefined
}

/**
 * Redirect links to public of kids page when needed
 */

type LinkEnv = {
  isPublic: boolean
  isKids: boolean
  isKidsPreview: boolean
  submittedId?: string | number | null
  examineId?: string | number | null
  reopenedId?: string | number | null
}

export default function useLinks() {
  const env = useContext(EnvContext)
  if (!env) throw new Error("EnvContext not found")
  return useCallback(
    (route: string, type?: "lesson-page") => {
      return buildLink(
        {
          isPublic: env.isPublic,
          isKids: env.isKids,
          isKidsPreview: env.isKidsPreview,
          submittedId: env.isSubmitted,
          examineId: env.isExamine,
          reopenedId: env.isReopened,
        },
        route,
        type
      )
    },
    [env]
  )
}

const prefixUrl =
  (prefix: string) =>
  <T extends RouteUrlObject>(id: string | number, oldRoute?: T | string) => {
    let href = typeof oldRoute === "string" ? oldRoute : oldRoute?.href
    if (!href) return `/${prefix}/${id}`
    if (href) {
      return `/${prefix}/${id}${href}`
    }
    return href
  }

export const prefixSubmitted = prefixUrl("ingeleverd")
export const prefixExamine = prefixUrl("nakijken")
export const prefixReopened = prefixUrl("heropened")

export function buildLink(
  env: LinkEnv,
  oldRoute: string,
  type?: "lesson-page"
) {
  let href = oldRoute
  if (env.isPublic && href) {
    href = `/publiek${href}`
  }
  if (env.isKidsPreview) {
    if (href && !href.includes("/kids/preview")) {
      href = "/kids/preview" + href
    }
  } else if (env.isKids) {
    if (href) {
      href = href.replace("/kids/preview", "") || "/"
    }
  }
  if (env.submittedId && type !== "lesson-page") {
    return prefixSubmitted(env.submittedId, href)
  }
  if (env.examineId && type !== "lesson-page") {
    return prefixExamine(env.examineId, href)
  }
  if (env.reopenedId && type !== "lesson-page") {
    return prefixReopened(env.reopenedId, href)
  }
  return href
}

export function getLink(
  query: ParsedUrlQuery,
  oldRoute: string,
  type?: "lesson-page"
) {
  return buildLink(
    {
      isPublic: getIsPublic(query),
      isKids: query.__subdomain === "kids",
      isKidsPreview: query.__kidsPreview === "true",
      submittedId: query.__submitted as string | undefined,
      examineId: query.__examine as string | undefined,
      reopenedId: query.__reopened as string | undefined,
    },
    oldRoute,
    type
  )
}
